/* eslint-disable prefer-const */
import { React, useState } from "react";

import PropTypes from "prop-types";
import ClayIcon from "@clayui/icon";
import ClayButton from "@clayui/button";

import moment from "moment";
import "moment-timezone";

/**
 * Render del calendario.
 *
 * @param props - Propiedades del componente.
 *
 * @returns Render react.
 */
function Calendar(props) {

  // ==========================
  // Propiedades del componente
  // ==========================

  const {
    doctorTimeZone, userTimeZone, appointments, schedule, setLocalSlot, setStep,
  } = props;

  // ======================
  // Estados del componente
  // ======================

  const [currentDate, setCurrentDate] = useState(new Date());

  const locale = Liferay.ThemeDisplay.getLanguageId().replace("_", "-");

  // ========================
  // Funciones del componente
  // ========================

  /**
   * Mapeo de fecha a español.
   *
   * @param day - Día a evaluar.
   *
   * @returns Número de día.
   */
  function mapSpanishDayToNumber(day) {

    const dayMap = {
      monday: 1, // Monday
      tuesday: 2, // Tuesday
      wednesday: 3, // Wednesday
      thursday: 4, // Thursday
      friday: 5, // Friday
      saturday: 6, // Saturday
      sunday: 0, // Sunday
    };

    return dayMap[day];

  }

  const allowedDayNumbers = (specificSchedule) => specificSchedule.days
    .map((day) => mapSpanishDayToNumber(day.key)).join(",");

  // Helper function to add days to a date
  const addDays = (date, days) => {

    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;

  };

  // Format date as 'Day, day month' according to locale
  const formatDate = (date, localez) => {

    const options = { weekday: "short", day: "numeric", month: "short" };
    return new Intl.DateTimeFormat(localez, options).format(date);

  };

  // Check if the day is allowed
  const isDayAllowed = (date, specificSchedule) => {

    const dayOfWeek = date.getDay(); // This returns 0 for Sunday through 6 for Saturday
    return allowedDayNumbers(specificSchedule).split(",").includes(dayOfWeek.toString());

  };

  // Check if the specific time slot is reserved
  const isTimeReserved = (date, time) => {

    // Crear dateTime en la zona horaria del usuario
    const dateTime = moment.tz(date, userTimeZone)
      .hour(time.split(":")[0]).minute(time.split(":")[1]);

    return appointments?.some((appointment) => {

      const start = moment.tz(appointment.startDate, userTimeZone);
      const end = moment.tz(appointment.endDate, userTimeZone);

      return dateTime.isBetween(start, end, null, "[]");

    });

  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  function generateTimeBlocks(startTime, endTime, increment) {

    // Parse the start time and end time into moment objects in the doctor's timezone
    let time = moment.tz({ hour: startTime, minute: 0 }, doctorTimeZone);
    const end = moment.tz({ hour: endTime, minute: 0 }, doctorTimeZone);

    // Parsing the increment, which might be in the form of "HH:mm" or just "mm"
    const incrementParts = increment.split(":");
    const incrementDuration = moment.duration({
      hours: incrementParts.length === 2 ? parseInt(incrementParts[0], 10) : 0,
      minutes: parseInt(incrementParts[incrementParts.length - 1], 10),
    });

    const timeBlocks = [];

    // Loop until the current time is after the end time
    while (!time.isAfter(end)) {

      // Convert the time to the viewer's timezone before pushing
      timeBlocks.push(time.clone().tz(userTimeZone).format("HH:mm"));
      time.add(incrementDuration); // Add duration to the current time

    }

    // Ensure that if the exact end time matches the last increment, it is included
    if (time.clone().tz(userTimeZone).format("HH:mm")
      === end.clone().tz(userTimeZone).format("HH:mm")) {

      timeBlocks.push(time.format("HH:mm"));

    }

    return timeBlocks;

  }

  /**
   * Genera días dinámicamente.
   *
   * @param specificSchedule - Configuración de agenda.
   *
   * @returns Arreglo de días.
   */
  function generateDays(specificSchedule) {

    const days = [];

    for (let i = 0; i < 5; i += 1) {

      const date = addDays(currentDate, i);

      const formattedDate = formatDate(date, locale);

      const timeBlock = generateTimeBlocks(
        specificSchedule.startTime,
        specificSchedule.endTime,
        specificSchedule.appointmentDuration.key,
      );

      days.push({
        dayName: (formattedDate.split(",")[0])
          .charAt(0).toUpperCase() + (formattedDate.split(",")[0]).slice(1),
        date: formattedDate.substring(formattedDate.indexOf(" ") + 1),
        times: timeBlock.map((time) => ({
          date,
          startTime: time,
          duration: parseInt(specificSchedule.appointmentDuration.key, 10),
          isAllowed: isDayAllowed(date, specificSchedule) && !isTimeReserved(date, time),
        })),
      });

    }
    return days;

  }

  // Move date range forward or backward
  const handleDateChange = (direction) => {

    setCurrentDate(addDays(currentDate, direction === "next" ? 5 : -5));

  };

  /**
   * Hace merge de los horarios.
   *
   * @param dayArrays - Array de horarios resultante.
   *
   * @returns Horarios en merge.
   */
  function mergeDayResults(dayArrays) {

    const mergedDays = {};

    dayArrays.forEach((days) => {

      days.forEach((day) => {

        if (!mergedDays[day.dayName]) {

          // Initialize this day if it doesn't exist
          mergedDays[day.dayName] = { ...day };

        } else {

          // Merge the times array
          day.times.forEach((newTime) => {

            const existingTime = mergedDays[day.dayName]
              .times.find((t) => t.startTime === newTime.startTime);

            if (existingTime) {

              // Merge the reservation and allowance statuses
              existingTime.isAllowed = existingTime.isAllowed || newTime.isAllowed;

            } else {

              // If the time slot doesn't exist, add it
              mergedDays[day.dayName].times.push(newTime);

            }

          });

        }

      });

    });

    return Object.values(mergedDays); // Convert back to array if needed

  }

  /**
   * Función principal.
   *
   * @returns Días en merge.
   */
  function runAndMerge() {

    const allResults = [];

    for (let index = 0; index < schedule.length; index += 1) {

      allResults.push(generateDays(schedule[index]));

    }

    return mergeDayResults(allResults);

  }

  const days = runAndMerge();

  // =======================
  // Template del componente
  // =======================

  return (

    <div className="row">

      <div className="col-auto px-0">

        <ClayButton
          displayType="link"
          size="sm"
          onClick={() => handleDateChange("prev")}
          disabled={moment(currentDate).isSame(moment(), "day")}
        >

          <ClayIcon
            spritemap={Liferay.Icons.spritemap}
            symbol="dr-now-angle-left"
          />

        </ClayButton>

      </div>

      <div className="col p-0">

        <div className="row">

          {days.map((day) => (
            <div className="col px-1">

              <div className="row text-center">
                <div className="col">

                  <div className="row">
                    <div className="col">

                      <span className="text-center text-primary text-1 font-weight-bold">
                        {day.dayName}
                      </span>

                    </div>
                  </div>

                  <div className="row mt-n1">
                    <div className="col">

                      <span className="text-center text-gray-800 mb-2 text-2">
                        {day.date}
                      </span>

                    </div>
                  </div>

                </div>
              </div>

              {day.times.map((localSlot) => (

                <div className="row mb-2">
                  <div className="col">

                    <ClayButton
                      block
                      size="sm"
                      displayType="info"
                      className="text-1 font-weight-light btn-appointment px-1 px-md-2"
                      disabled={!localSlot.isAllowed
                          || moment().isAfter(moment(localSlot.date)
                            .hour(localSlot.startTime.split(":")[0])
                            .minute(localSlot.startTime.split(":")[1]))}
                      onClick={() => {

                        const start = `${localSlot.date.toISOString()
                          .split("T")[0]}T${localSlot.startTime}:00.000Z`;

                        const s = new Date(start);
                        const end = new Date(s.setMinutes(s.getMinutes() + localSlot.duration));

                        setLocalSlot({ rawDate: start, startdate: new Date(start), endDate: end });
                        setStep(1);

                      }}
                    >

                      {localSlot.startTime}

                    </ClayButton>

                  </div>
                </div>

              ))}

            </div>
          ))}
        </div>

      </div>

      <div className="col-auto px-0">

        <ClayButton
          displayType="link"
          borderless
          size="sm"
          onClick={() => handleDateChange("next")}
        >

          <ClayIcon
            spritemap={Liferay.Icons.spritemap}
            symbol="dr-now-angle-right"
          />

        </ClayButton>

      </div>

    </div>

  );

}

Calendar.propTypes = {
  doctorTimeZone: PropTypes.shape.isRequired,
  userTimeZone: PropTypes.shape.isRequired,
  appointments: PropTypes.shape.isRequired,
  schedule: PropTypes.shape.isRequired,
  setLocalSlot: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
};

export default Calendar;
